/**
 * CCO › Qualidade dos sensores: a saúde de cada sensor num só lugar (US-3010) — Nexora Grid.
 *
 * Criado em: 25/10/2026 · Última modificação: 25/10/2026 · Versão: 1.0.0
 *
 * Uma linha por sensor (dispositivo × medida) com a última leitura, a situação (ok, sem comunicação, suspeito, travado,
 * saturado), desde quando e o motivo, e o % de leituras suspeitas em 24 h e 7 dias — calculado no hub
 * (`hub_qualidade_sensores`). A engenharia da operadora (e a Nexora) ajusta um sensor: faixa física, escala do
 * transmissor e detecção de travado/pico. O ajuste vale no hub no próximo ciclo da ingestão (até 5 min).
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { useCco } from "./contexto";

type Ajuste = { versao: number; faixa_minimo: number | null; faixa_maximo: number | null; escala_minimo: number | null;
  escala_maximo: number | null; detectar_travado: boolean | null; travado_horas: number | null; detectar_pico: boolean | null;
  motivo: string; atualizado_por: string; atualizado_em: string };
type Situacao = "ok" | "sem_comunicacao" | "suspeito" | "travado" | "saturado";
type Sensor = { device_id: string; medida: string; identificacao: string | null; setor: string | null; ultima_leitura: string | null;
  ultimo_valor: number | null; situacao: Situacao; desde: string | null; motivo: string; pct_suspeitas_24h: number | null;
  pct_suspeitas_7d: number | null; sem_sinal_min: number; ajuste: Ajuste | null };
type Resposta = { sensores: Sensor[]; resumo: Partial<Record<Situacao, number>>; pode_ajustar: boolean };
type Formulario = { faixa_minimo: string; faixa_maximo: string; escala_minimo: string; escala_maximo: string;
  detectar_travado: "" | "sim" | "nao"; travado_horas: string; detectar_pico: "" | "sim" | "nao"; motivo: string };

export const SITUACAO_SENSOR: Record<Situacao, [string, string]> = {
  sem_comunicacao: ["Sem comunicação", "nx-selo-sem-comunicacao"], saturado: ["Saturado", "situacao-pendente"],
  travado: ["Travado", "situacao-pendente"], suspeito: ["Suspeito", "situacao-pendente"], ok: ["Ok", "situacao-validado"] };
const ORDEM: Situacao[] = ["sem_comunicacao", "saturado", "travado", "suspeito", "ok"];
const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const pct = (v: number | null) => (v === null ? "—" : `${v.toLocaleString("pt-BR")}%`);
const texto = (v: number | null) => (v === null ? "" : String(v));
const numero = (v: string) => (v.trim() === "" ? null : Number(v.replace(",", ".")));
const simNao = (v: boolean | null): "" | "sim" | "nao" => (v === null ? "" : v ? "sim" : "nao");
const booleano = (v: "" | "sim" | "nao") => (v === "" ? null : v === "sim");

export function Qualidade() {
  const { empresa } = useCco();
  const [dados, setDados] = useState<Resposta | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Situacao | "problemas" | null>("problemas");
  const [edicao, setEdicao] = useState<{ sensor: Sensor; form: Formulario } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(() => {
    if (!empresa) return;
    api<Resposta>(`/v1/qualidade/sensores?empresa=${empresa}`).then((r) => { setDados(r); setErro(null); })
      .catch((x) => setErro(x instanceof ErroApi ? x.message : "Falha de rede."));
  }, [empresa]);
  useEffect(() => { carregar(); const r = window.setInterval(carregar, 60000); return () => window.clearInterval(r); }, [carregar]);

  const abrir = (s: Sensor) => {
    const a = s.ajuste;
    setAviso(null);
    setEdicao({ sensor: s, form: { faixa_minimo: texto(a?.faixa_minimo ?? null), faixa_maximo: texto(a?.faixa_maximo ?? null),
      escala_minimo: texto(a?.escala_minimo ?? null), escala_maximo: texto(a?.escala_maximo ?? null),
      detectar_travado: simNao(a?.detectar_travado ?? null), travado_horas: texto(a?.travado_horas ?? null),
      detectar_pico: simNao(a?.detectar_pico ?? null), motivo: "" } });
  };
  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    if (!edicao || !empresa) return;
    const { sensor, form } = edicao;
    try {
      await api("/v1/qualidade/ajustes", { metodo: "PUT", corpo: { empresa, device_id: sensor.device_id, medida: sensor.medida,
        faixa_minimo: numero(form.faixa_minimo), faixa_maximo: numero(form.faixa_maximo), escala_minimo: numero(form.escala_minimo),
        escala_maximo: numero(form.escala_maximo), detectar_travado: booleano(form.detectar_travado), travado_horas: numero(form.travado_horas),
        detectar_pico: booleano(form.detectar_pico), motivo: form.motivo, versao: sensor.ajuste?.versao } });
      setAviso(`Ajuste de ${sensor.identificacao ?? sensor.device_id} · ${sensor.medida} salvo: vale no hub em até 5 minutos.`);
      setEdicao(null);
      carregar();
    } catch (x) {
      if (x instanceof ErroApi && x.status === 409) { setAviso("Outra pessoa alterou este ajuste antes. Os dados foram recarregados."); setEdicao(null); carregar(); }
      else setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede.");
    }
  };
  const remover = async () => {
    if (!edicao || !empresa) return;
    const { sensor, form } = edicao;
    try {
      await api(`/v1/qualidade/ajustes?empresa=${empresa}&device_id=${sensor.device_id}&medida=${sensor.medida}&motivo=${encodeURIComponent(form.motivo)}`,
        { metodo: "DELETE" });
      setAviso(`${sensor.identificacao ?? sensor.device_id} · ${sensor.medida} voltou ao padrão.`);
      setEdicao(null);
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };

  const lista = (dados?.sensores ?? []).filter((s) => !filtro || (filtro === "problemas" ? s.situacao !== "ok" : s.situacao === filtro));
  const campo = (nome: keyof Formulario, rotulo: string, props: Record<string, unknown> = {}) => edicao && (
    <div className="nx-campo"><label htmlFor={`qs-${nome}`}>{rotulo}</label>
      <input id={`qs-${nome}`} inputMode="decimal" value={edicao.form[nome]} placeholder="padrão" {...props}
        onChange={(e) => setEdicao({ ...edicao, form: { ...edicao.form, [nome]: e.target.value } })} /></div>);
  const escolha = (nome: "detectar_travado" | "detectar_pico", rotulo: string) => edicao && (
    <div className="nx-campo"><label htmlFor={`qs-${nome}`}>{rotulo}</label>
      <select id={`qs-${nome}`} value={edicao.form[nome]} onChange={(e) => setEdicao({ ...edicao, form: { ...edicao.form, [nome]: e.target.value as Formulario[typeof nome] } })}>
        <option value="">padrão</option><option value="sim">sim</option><option value="nao">não</option>
      </select></div>);

  return (
    <section className="seguranca qualidade-sensores" aria-label="Qualidade dos sensores">
      <div className="nx-cartao">
        <h3>Qualidade dos sensores</h3>
        <div className="contadores-seguranca" role="group" aria-label="Filtro por situação">
          {ORDEM.map((s) => (
            <button key={s} type="button" className={`nx-cartao contador-seg${filtro === s ? " ativo" : ""}`} aria-pressed={filtro === s}
              onClick={() => setFiltro(filtro === s ? null : s)}>
              <span className="nx-kpi">{dados ? (dados.resumo[s] ?? 0) : "—"}</span><small>{SITUACAO_SENSOR[s][0]}</small>
            </button>
          ))}
          <button type="button" className={`nx-cartao contador-seg${filtro === "problemas" ? " ativo" : ""}`} aria-pressed={filtro === "problemas"}
            onClick={() => setFiltro(filtro === "problemas" ? null : "problemas")}>
            <span className="nx-kpi">{dados ? dados.sensores.filter((s) => s.situacao !== "ok").length : "—"}</span><small>Com problema</small>
          </button>
        </div>
        <p className="nx-suave">Suspeito: a última leitura está fora da faixa física ou é um pico isolado, ou 10% ou mais das leituras de 24 h foram suspeitas (ficam guardadas, mas fora das médias). Travado: o mesmo valor por horas numa medida que costuma variar. Saturado: colado no limite da escala do transmissor. Sem comunicação: passou do tempo sem sinal do cadastro do módulo.</p>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
        {edicao && (
          <form className="form-filial" aria-label="Ajuste do sensor" onSubmit={(e) => void salvar(e)}>
            <p><b>{edicao.sensor.identificacao ?? edicao.sensor.device_id}</b> · {edicao.sensor.medida} — campo vazio = padrão do sistema</p>
            {campo("faixa_minimo", "Faixa física: mínimo")}{campo("faixa_maximo", "Faixa física: máximo")}
            {campo("escala_minimo", "Escala do transmissor: mínimo")}{campo("escala_maximo", "Escala do transmissor: máximo")}
            {escolha("detectar_travado", "Avisar sensor travado")}{campo("travado_horas", "Travado após (h)")}
            {escolha("detectar_pico", "Marcar picos")}
            <div className="nx-campo"><label htmlFor="qs-motivo">Motivo</label>
              <input id="qs-motivo" required minLength={5} maxLength={300} value={edicao.form.motivo} placeholder="ex.: laudo do transmissor"
                onChange={(e) => setEdicao({ ...edicao, form: { ...edicao.form, motivo: e.target.value } })} /></div>
            <button type="submit" className="nx-botao">Salvar ajuste</button>
            {edicao.sensor.ajuste && <button type="button" className="nx-botao" disabled={edicao.form.motivo.trim().length < 5} onClick={() => void remover()}>Voltar ao padrão</button>}
            <button type="button" className="nx-botao" onClick={() => setEdicao(null)}>Cancelar</button>
          </form>
        )}
        {!dados && !erro && <p className="nx-suave">carregando…</p>}
        {dados && (
          <div className="tabela-rolagem">
            <table className="tabela-composicao tabela-seguranca">
              <thead><tr><th>Sensor</th><th>Setor</th><th>Última leitura</th><th>Situação</th><th>Desde</th><th>Motivo</th>
                <th>Suspeitas 24 h</th><th>Suspeitas 7 dias</th>{dados.pode_ajustar && <th>Ajuste</th>}</tr></thead>
              <tbody>
                {lista.map((s) => (
                  <tr key={`${s.device_id}/${s.medida}`}>
                    <td><b>{s.identificacao ?? s.device_id}</b><br /><small className="nx-suave">{s.medida}</small></td>
                    <td>{s.setor || "—"}</td>
                    <td className="nx-valor">{s.ultimo_valor === null ? "—" : s.ultimo_valor.toLocaleString("pt-BR", { maximumFractionDigits: 3 })}<br />
                      <small className="nx-suave">{quando(s.ultima_leitura)}</small></td>
                    <td><span className={`nx-selo ${SITUACAO_SENSOR[s.situacao][1]}`}>{SITUACAO_SENSOR[s.situacao][0]}</span></td>
                    <td className="nx-valor">{quando(s.desde)}</td>
                    <td><small>{s.motivo || "—"}</small>{s.ajuste && <><br /><small className="nx-suave">ajustado: {s.ajuste.motivo}</small></>}</td>
                    <td className="nx-valor">{pct(s.pct_suspeitas_24h)}</td>
                    <td className="nx-valor">{pct(s.pct_suspeitas_7d)}</td>
                    {dados.pode_ajustar && <td><button type="button" className="nx-botao" aria-label={`Ajustar ${s.identificacao ?? s.device_id} ${s.medida}`}
                      onClick={() => abrir(s)}>{s.ajuste ? "Editar" : "Ajustar"}</button></td>}
                  </tr>
                ))}
                {!lista.length && <tr><td colSpan={9} className="nx-suave">nenhum sensor nesta situação</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
